/**
 * RelayLoadCarouselCard — one relay load (billboard panel / lighting zone),
 * shown as the active face of the relay card's load carousel.
 *
 * A fixed-height filled card so paging between loads never shifts the relay
 * card's layout. Content is grouped and centered against that constant height:
 * the load name (with its compass direction), a muted description + fixture
 * line, and the contract status pinned along the bottom. Tapping it opens the
 * relay's edit screen. Purely presentational — the parent RelayLoadCarousel
 * owns paging and resolves the fixture/contract values.
 */
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import React from 'react';

// Constant card height so sparse loads (name only) and full ones (name +
// description + fixture + contract) occupy the same vertical space.
export const CARD_HEIGHT = 96;

const ELLIPSIS = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

function RelayLoadCarouselCard({ name, direction, description, fixtureName, advertiser, contractLine, onClick }) {
  const hasDescription = Boolean(description) && description !== '-';
  const hasDirection = Boolean(direction);
  const hasFixture = Boolean(fixtureName);
  // Two-column layout — Name/Description on the left, Direction/Fixture on the
  // right — only when a row actually pairs left & right: a direction (pairs with
  // the name) or a description + fixture (pairs on the second row). Otherwise the
  // content centers (e.g. name only, name + description, or name + fixture).
  const isSplit = hasDirection || (hasDescription && hasFixture);
  const hasMetaRow = hasDescription || hasFixture;
  const hasContract = Boolean(advertiser) || Boolean(contractLine);

  const align = isSplit ? 'left' : 'center';

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Full-width rows (justify left↔right), with the whole content block
        // centered vertically so the card fills its fixed height evenly instead
        // of clustering everything at the top.
        alignItems: 'stretch',
        justifyContent: 'center',
        width: '100%',
        height: CARD_HEIGHT,
        bgcolor: 'action.hover',
        borderRadius: 3,
        // Horizontal padding clears the carousel's overlaid chevron hit-targets
        // (RelayLoadCarousel: size="small" IconButtons, ~30px wide, inset 2px
        // from each edge) so no value renders underneath them; text that's
        // still too long past that inset ellipsizes (tooltip carries the full
        // value).
        py: 2,
        px: 4.5,
        transition: 'background-color var(--duration-normal) var(--easing-standard)',
        '&:hover': { bgcolor: 'action.selected' },
      }}
    >
      {/* Name + meta block; the card centers this and the contract as a group. */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Tooltip title={name ?? ''} disableHoverListener={!name}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, textAlign: align, ...ELLIPSIS }}>
              {name}
            </Typography>
          </Tooltip>
          {hasDirection ? (
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
              {direction}
            </Typography>
          ) : null}
        </Box>

        {hasMetaRow ? (
          isSplit ? (
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mt: 0.25 }}>
              <Tooltip title={hasDescription ? description : ''} disableHoverListener={!hasDescription}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, textAlign: 'left', ...ELLIPSIS }}>
                  {hasDescription ? description : ''}
                </Typography>
              </Tooltip>
              {hasFixture ? (
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {fixtureName}
                </Typography>
              ) : null}
            </Box>
          ) : (
            // Centered: only one of description / fixture is present here (both
            // would have triggered the split), so show whichever it is.
            <Tooltip
              title={hasDescription ? description : (fixtureName ?? '')}
              disableHoverListener={!hasDescription && !fixtureName}
            >
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 0.25, ...ELLIPSIS }}>
                {hasDescription ? description : fixtureName}
              </Typography>
            </Tooltip>
          )
        ) : null}
      </Box>

      {hasContract ? (
        <Box sx={{ flexShrink: 0, textAlign: 'center', mt: 0.75 }}>
          {advertiser ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {advertiser}
            </Typography>
          ) : null}
          {contractLine ? (
            <Typography variant="caption" component="div">
              {contractLine}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </ButtonBase>
  );
}

export default RelayLoadCarouselCard;
